import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { FAQS } from "@/constants/faqs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Download, LifeBuoy } from "lucide-react";

const EMPTY = { question: "", quickAnswer: "", sections: [{ label: "Why?", help: false, paragraphs: [] }], published: true };
const toText = (ps) => (ps || []).join("\n\n");
const fromText = (t) => t.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

function SectionEditor({ sec, idx, onChange, onRemove }) {
  return (
    <div data-testid={`faq-section-editor-${idx}`} className="rounded-xl border border-cream-deep p-3 space-y-2 bg-cream">
      <div className="flex flex-wrap items-center gap-3">
        <Input data-testid={`faq-section-label-${idx}`} className="h-8 max-w-[220px]" value={sec.label} placeholder="Section heading, e.g. Why?" onChange={(e) => onChange({ ...sec, label: e.target.value })} />
        <label className="flex items-center gap-2 text-xs"><Switch checked={sec.help} onCheckedChange={(v) => onChange({ ...sec, help: v })} /><LifeBuoy className="h-3.5 w-3.5 text-coral" />"Get help" style</label>
        <Button type="button" size="sm" variant="ghost" onClick={onRemove} className="ml-auto h-8 text-rag-red rounded-full"><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
      <Textarea data-testid={`faq-section-text-${idx}`} rows={4} value={sec._text ?? toText(sec.paragraphs)} placeholder="Write the detail here. Leave a blank line between paragraphs." onChange={(e) => onChange({ ...sec, _text: e.target.value, paragraphs: fromText(e.target.value) })} />
    </div>
  );
}

function FaqDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...initial, sections: (initial.sections || []).map((s) => ({ ...s })) } : { ...EMPTY, sections: [{ label: "Why?", help: false, paragraphs: [] }] }); }, [open, initial]);
  const setSec = (i, v) => setF({ ...f, sections: f.sections.map((s, k) => (k === i ? v : s)) });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { question: f.question, quickAnswer: f.quickAnswer, published: f.published, sections: f.sections.filter((s) => s.label.trim()).map(({ label, help, paragraphs }) => ({ label, help: !!help, paragraphs })) };
    try {
      if (initial?.id) await api.put(`/admin/faqs/${initial.id}`, payload); else await api.post("/admin/faqs", payload);
      toast.success(initial?.id ? "FAQ updated" : "FAQ added"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="faq-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-brand">{initial?.id ? "Edit FAQ" : "Add FAQ"}</DialogTitle><DialogDescription>The quick answer is always visible; sections sit behind expandable panels.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Question</Label><Input data-testid="faq-question" required minLength={5} value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} /></div>
          <div><Label>Quick answer</Label><Textarea data-testid="faq-quick-answer" required minLength={5} rows={2} value={f.quickAnswer} onChange={(e) => setF({ ...f, quickAnswer: e.target.value })} /></div>
          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label>Detail sections</Label><Button type="button" size="sm" variant="outline" data-testid="faq-add-section" onClick={() => setF({ ...f, sections: [...f.sections, { label: "", help: false, paragraphs: [] }] })} className="h-8 rounded-full"><Plus className="h-3.5 w-3.5 mr-1" />Add section</Button></div>
            {f.sections.map((s, i) => <SectionEditor key={`sec-${i}-${f.sections.length}`} sec={s} idx={i} onChange={(v) => setSec(i, v)} onRemove={() => setF({ ...f, sections: f.sections.filter((_, k) => k !== i) })} />)}
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="faq-published" checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} />Published on the FAQ page</label>
            <Button type="submit" data-testid="faq-save" disabled={busy} className="rounded-full bg-brand hover:bg-brand-hover text-white">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function FaqAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/faqs").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const importBuiltIn = async () => {
    try { await api.post("/admin/faqs/import", FAQS.map(({ question, quickAnswer, sections }) => ({ question, quickAnswer, sections: sections.map(({ label, help, paragraphs }) => ({ label, help: !!help, paragraphs })), published: true }))); toast.success("Built-in FAQs imported — you can now edit them"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[i], next[j]] = [next[j], next[i]]; setItems(next);
    try { await api.put("/admin/faqs/reorder", next.map((t) => t.id)); } catch { toast.error("Reorder failed"); load(); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.question}"?`)) return;
    try { await api.delete(`/admin/faqs/${t.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggle = async (t) => {
    try { await api.put(`/admin/faqs/${t.id}`, { question: t.question, quickAnswer: t.quickAnswer, sections: t.sections, published: !t.published }); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (items === null) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-muted-foreground">{items.length === 0 ? "The FAQ page is currently showing the 10 built-in questions. Import them to start editing, or add your own." : "Published questions appear on the FAQ page in this order."}</p>
        <div className="flex gap-2">
          {items.length === 0 && <Button size="sm" variant="outline" data-testid="faq-import-btn" onClick={importBuiltIn} className="rounded-full"><Download className="h-4 w-4 mr-1" />Import built-in FAQs</Button>}
          <Button size="sm" data-testid="faq-add-btn" onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-coral hover:bg-coral-hover text-white"><Plus className="h-4 w-4 mr-1" />Add FAQ</Button>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((t, i) => (
          <div key={t.id} data-testid={`faq-row-${i}`} className={`bg-cream-card border rounded-xl p-4 flex gap-4 ${t.published ? "border-cream-deep" : "border-dashed border-cream-deep opacity-70"}`}>
            <div className="flex flex-col gap-1 shrink-0">
              <Button size="icon" variant="ghost" data-testid={`faq-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="h-7 w-7"><ArrowUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" data-testid={`faq-down-${i}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="h-7 w-7"><ArrowDown className="h-4 w-4" /></Button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-brand">{i + 1}. {t.question}</div>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.quickAnswer}</p>
              <div className="text-xs text-muted-foreground mt-2">{(t.sections || []).length} section{(t.sections || []).length === 1 ? "" : "s"}{!t.published && " · Draft (hidden)"}</div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <label className="flex items-center gap-2 text-xs"><Switch data-testid={`faq-toggle-${i}`} checked={t.published} onCheckedChange={() => toggle(t)} />Published</label>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" data-testid={`faq-edit-${i}`} onClick={() => { setEditing(t); setOpen(true); }} className="h-8 rounded-full"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" data-testid={`faq-delete-${i}`} onClick={() => remove(t)} className="h-8 rounded-full text-rag-red"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <FaqDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}
